import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, ViewEncapsulation, Inject } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { cloneDeep } from 'lodash';
import { FiltersService } from 'src/app/core/services/filters.service';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';

@Component({
    selector: 'app-filter-select',
    templateUrl: './filter-select.component.html',
    styleUrls: ['./filter-select.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FilterSelectComponent {
  // Get the element with #scrollViewport
  @ViewChild('scrollViewport') scrollViewport: CdkVirtualScrollViewport|undefined;

  @Input() divDataView: string = "";
  @Input() title: string = "Region";
  @Input() isMultiple: boolean = false;
  @Input() required: boolean = true;
  @Input() disabled: boolean = false;
  @Input() hidden: boolean = false;
  @Input() isLoading: boolean = false;

  // All options available for selection
  @Input() optionList: string[] = [];

  // Start state of the selected options
  @Input() currentlySelected: string[] = [];

  DOMSelected: string[] = [];

  listToDisplay: string[] = [];

  @Output() sendSelect = new EventEmitter<string[]>();

  selectedPeriod: string = '';
  // current view of the select box. Available options are 'all' and 'view selected'
  currentView: string = 'all';
  // search input for filtering the options. Default is empty
  searchInput: string = '';

  excludeDeletedAccounts: boolean = true;

  isSelectAllChecked: boolean = false

  selectedRegion: string = '';

  constructor(
    private filtersService: FiltersService,
    @Inject(FEATURE_FLAGS) private featureFlags: FeatureFlags,
  ) { }


  ngOnInit() { 
    this.initializeOptions() 
  }

  ngOnChanges(changes: SimpleChanges) { 
    if (changes["isLoading"] && this.isLoading) {
      this.isSelectAllChecked = false;
    }
    
    if (changes["optionList"] || changes["currentlySelected"] || changes["isLoading"]) { 
      this.initializeOptions();
    }
  }

  private initializeOptions() {
    this.listToDisplay = this.optionList;
    this.DOMSelected = cloneDeep(this.currentlySelected);

    // Temporary handling for single select 'Region' filter to update selectedRegion variable
    if (this.title === 'Region' && !this.isMultiple && this.currentlySelected.length > 0) {
        this.selectedRegion = this.currentlySelected[0];
    }

    // Filter the list to display as per search input
    this.filterDisplayList();
  }

  //#region On Input trigger functions
  onChange(change: MatSelectChange): void {
    if (this.title !== 'Region' && !this.isMultiple) this.selectOptions();

    if(this.title ==='Region' && !this.isMultiple) {
      // The 'regions' filter currently supports both single and multi-select modes.
      // For single-select, special handling is required to update the 'selectedRegion' variable to ensure correct display in the mat-select UI.
      // Once multi-select becomes the default for the region filter (post-globalisation), this special handling can be removed.
      this.selectedRegion = change.value;
      this.currentlySelected = [change.value];
    } else {
      // Get all the currently selected options from available options
      let DomSelected = change.source.options
        .filter((o: any) => o.selected)
        .map((o: any) => o.value);

      // remaining options: exclude source options from option List
      let remainingOptions = this.optionList.filter((o: string) => !change.source.options.map((o: any) => o.value).includes(o));

      // get current selected options from remaining options
      let currentSelected = remainingOptions.filter((o: string) => this.currentlySelected.includes(o));

      // join the two arrays to get the final selected options
      this.currentlySelected = [...DomSelected, ...currentSelected];

      // Update DOMSelected
      this.DOMSelected = cloneDeep(this.currentlySelected);
    }

    this.selectOptions();
  }

  onSearchInput() {
    this.filterDisplayList();
  }

  resetToTop() {
    if (this.scrollViewport) this.scrollViewport.scrollToIndex(0);
  }
  //#endregion

  //#region Search button functions
  updateSelectionFromCheckbox(event: Event) {
    if (this.isSelectAllChecked) {
      this.selectAllOptions();
    } else {
      this.clearAllOptions();
    }
  }

  selectAllOptions() {   
    // set currently selected as list to display
    let currentSelected = [...this.listToDisplay];
    // create a unique set of selected options
    this.currentlySelected = [...new Set([...this.currentlySelected, ...currentSelected])];
    this.DOMSelected = cloneDeep(this.currentlySelected);
    // update the overall selected options
    this.selectOptions();
  }

  clearAllOptions() {
    // remove all selected options that are in the list to display
    this.isSelectAllChecked = false;
    this.currentlySelected = this.currentlySelected.filter(
      (o: string) => !this.listToDisplay.includes(o)
    );
    this.DOMSelected = cloneDeep(this.currentlySelected);
    // update the overall selected options
    this.selectOptions();
  }

  viewSelectedOptions() {
    // Set current View as view selected
    this.currentView = 'view selected';
    // filter list to display as per search input
    this.filterDisplayList();
  }

  returnToAllOptions() {
    // Set current View as All
    this.currentView = 'all';
    // filter list to display as per search input
    this.filterDisplayList();
  }
  //#endregion

  selectOptions() {
    // call remember choices, use title as name for localstorage but remove the spaces
    this.filtersService.rememberPriorChoices(
      'ebr' +
      this.divDataView.replace(/\s/g, '') +
      this.title.replace(/\s/g, ''),
      this.currentlySelected.toString()
    );

    this.sendSelect.emit(this.currentlySelected);
  }

  filterDisplayList() {
    // filter as per searchInput and view selected. If searchInput is empty, show all options
    if (this.currentView == 'view selected') {
      // filter the list to display as per search input
      this.listToDisplay = this.currentlySelected.filter(
        (o: string) =>
          o.toUpperCase().includes(this.searchInput.toUpperCase())
      );
    } else {
      // filter the list to display as per search input
      this.listToDisplay = this.optionList.filter((o: string) =>
        o.toUpperCase().includes(this.searchInput.toUpperCase())
      );
    }
    
    // If the visible list has changed, uncheck the checkbox
    this.isSelectAllChecked = this.listToDisplay.every(option =>
      this.currentlySelected.includes(option)
    );
  }

  isOptionListEmpty(list: string[]) {
    return list.length == 0 || (list.length == 1 && list[0] == null);
  }

  getSanitizedFilterId(filterTitle:string): string {
    return 'select-'+ filterTitle.toLowerCase().replace(/\s+/g, '-');
  }
}
